/**
 * SYSTELOS MONITOR - Página de Detalhe do Cliente
 * Configuração de DEC (certificado) e DJE (tenant_id)
 * 
 * @version 2.6.0
 * @date 26/01/2026
 * ADD: Modal de instruções para obter credenciais DJE
 * HIDE: Seção de Notificações (notificação vai para o escritório, não cliente)
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Building2,
  ArrowLeft,
  RefreshCw,
  Mail,
  Phone,
  CheckCircle,
  XCircle,
  Save,
  Loader2,
  X,
  Upload,
  Shield,
  AlertTriangle,
  Trash2,
  FileKey,
  Scale,
  Wifi,
  Play,
  Clock,
  History,
  ChevronLeft,
  ChevronRight,
  HelpCircle,
  ExternalLink,
} from 'lucide-react'
import { AppLayout } from '@/components/layout'
import { PageContainer, PageBanner, PageSection } from '@/components/page'
import clientesService, { type HistoricoConsulta } from '@/services/clientesService'
import type { Cliente } from '@/types'
import toast from 'react-hot-toast'

// ============================================
// MODAL DE INSTRUÇÕES DJE
// ============================================
interface ModalInstrucoesDJEProps {
  isOpen: boolean
  onClose: () => void
}

function ModalInstrucoesDJE({ isOpen, onClose }: ModalInstrucoesDJEProps) {
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Scale className="w-5 h-5 text-purple-700" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Como obter as credenciais do DJE
                </h2>
                <p className="text-sm text-gray-500">Tempo estimado: 15-30 minutos</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-5 space-y-6">
            {/* Pré-requisitos */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <h3 className="font-medium text-amber-800 mb-2">📋 Pré-requisitos</h3>
              <ul className="space-y-1 text-sm text-amber-700">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-amber-600" />
                  Certificado Digital A1 (e-CNPJ) instalado no computador
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-amber-600" />
                  Conta Gov.br nível Prata ou Ouro
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-amber-600" />
                  PJeOffice Pro instalado e rodando
                </li>
              </ul>
            </div>

            {/* Passo 1 */}
            <div className="space-y-2">
              <h3 className="font-medium text-gray-900 flex items-center gap-2">
                <span className="w-6 h-6 bg-[#0F1B3D] text-white rounded-full flex items-center justify-center text-sm">1</span>
                Instalar o PJeOffice Pro
              </h3>
              <p className="text-sm text-gray-600 ml-8">
                O PJeOffice faz a ponte entre o certificado digital e o navegador.
              </p>
              <a
                href="https://pjeoffice.trf3.jus.br/pjeoffice-pro/docs/userguide.html#downloads"
                target="_blank"
                rel="noopener noreferrer"
                className="ml-8 inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
              >
                <ExternalLink className="w-4 h-4" />
                Baixar PJeOffice Pro
              </a>
              <p className="text-xs text-gray-500 ml-8">
                Após instalar, verifique se o ícone aparece na bandeja do sistema (perto do relógio).
              </p>
            </div>

            {/* Passo 2 */}
            <div className="space-y-2">
              <h3 className="font-medium text-gray-900 flex items-center gap-2">
                <span className="w-6 h-6 bg-[#0F1B3D] text-white rounded-full flex items-center justify-center text-sm">2</span>
                Acessar o Portal DJE
              </h3>
              <a
                href="https://domicilio-eletronico.pdpj.jus.br"
                target="_blank"
                rel="noopener noreferrer"
                className="ml-8 inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
              >
                <ExternalLink className="w-4 h-4" />
                https://domicilio-eletronico.pdpj.jus.br
              </a>
              <ul className="ml-8 text-sm text-gray-600 space-y-1">
                <li>• Clique em <strong>"Seu certificado digital"</strong></li>
                <li>• Selecione o certificado e-CNPJ da empresa</li>
                <li>• Digite a senha do certificado</li>
              </ul>
            </div>

            {/* Passo 3 */}
            <div className="space-y-2">
              <h3 className="font-medium text-gray-900 flex items-center gap-2">
                <span className="w-6 h-6 bg-[#0F1B3D] text-white rounded-full flex items-center justify-center text-sm">3</span>
                Solicitar Credenciais da API
              </h3>
              <ul className="ml-8 text-sm text-gray-600 space-y-1">
                <li>• Clique no <strong>ícone do usuário</strong> (canto superior direito)</li>
                <li>• Selecione <strong>"Gerenciar credenciais API"</strong></li>
                <li>• Aceite o Termo de Uso da Plataforma</li>
                <li>• Solicite a API <strong>"DJE - Domicílio Judicial Eletrônico"</strong></li>
                <li>• Marque os ambientes: <strong>Homologação + Produção</strong></li>
              </ul>
            </div>

            {/* Passo 4 */}
            <div className="space-y-2">
              <h3 className="font-medium text-gray-900 flex items-center gap-2">
                <span className="w-6 h-6 bg-[#0F1B3D] text-white rounded-full flex items-center justify-center text-sm">4</span>
                Preencher Formulário
              </h3>
              <ul className="ml-8 text-sm text-gray-600 space-y-1">
                <li>• CNPJ e dados da empresa</li>
                <li>• Responsável técnico (CPF, nome, email)</li>
                <li>• Justificativa: <em>"Sistema de monitoramento de comunicações processuais"</em></li>
              </ul>
            </div>

            {/* Passo 5 */}
            <div className="space-y-2">
              <h3 className="font-medium text-gray-900 flex items-center gap-2">
                <span className="w-6 h-6 bg-[#0F1B3D] text-white rounded-full flex items-center justify-center text-sm">5</span>
                Assinar Termo no Gov.br
              </h3>
              <ul className="ml-8 text-sm text-gray-600 space-y-1">
                <li>• Baixe o termo PDF gerado</li>
                <li>• Acesse o assinador Gov.br</li>
                <li>• Assine digitalmente e faça upload do termo assinado</li>
              </ul>
              <a
                href="https://assinador.iti.br"
                target="_blank"
                rel="noopener noreferrer"
                className="ml-8 inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
              >
                <ExternalLink className="w-4 h-4" />
                https://assinador.iti.br
              </a>
            </div>

            {/* Passo 6 */}
            <div className="space-y-2">
              <h3 className="font-medium text-gray-900 flex items-center gap-2">
                <span className="w-6 h-6 bg-[#0F1B3D] text-white rounded-full flex items-center justify-center text-sm">6</span>
                Copiar Credenciais
              </h3>
              <ul className="ml-8 text-sm text-gray-600 space-y-1">
                <li>• A aprovação é <strong>automática e instantânea!</strong></li>
                <li>• Acesse o menu <strong>"Credenciais"</strong> → Visualizar</li>
                <li>• Copie o <strong>Tenant ID</strong> (geralmente é o CNPJ)</li>
              </ul>
            </div>

            {/* Aviso importante */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-red-800">Importante!</p>
                  <p className="text-sm text-red-700">
                    O <strong>Secret ID</strong> é mostrado apenas <strong>UMA VEZ</strong>! 
                    Copie e guarde em local seguro imediatamente.
                  </p>
                </div>
              </div>
            </div>

            {/* Links úteis */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-medium text-gray-700 mb-2">🔗 Links úteis</h4>
              <div className="space-y-1">
                <a
                  href="https://domicilio-eletronico.pdpj.jus.br"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-sm text-blue-600 hover:underline"
                >
                  Portal DJE (Produção)
                </a>
                <a
                  href="https://pjeoffice.trf3.jus.br/pjeoffice-pro/docs/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-sm text-blue-600 hover:underline"
                >
                  Download PJeOffice Pro
                </a>
                <a
                  href="https://assinador.iti.br"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-sm text-blue-600 hover:underline"
                >
                  Assinador Gov.br
                </a>
                <a
                  href="https://docs.pdpj.jus.br/servicos-negociais/domicilio-judicial-eletronico/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-sm text-blue-600 hover:underline"
                >
                  Documentação API DJE
                </a>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-gray-50 border-t px-6 py-4 rounded-b-xl">
            <button
              onClick={onClose}
              className="w-full px-4 py-2 text-sm font-medium text-white rounded-lg"
              style={{ backgroundColor: '#0F1B3D' }}
            >
              Entendi
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ============================================
// COMPONENTE PRINCIPAL
// ============================================
export default function ClienteDetalhe() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const refreshIntervalRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Estados
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [cliente, setCliente] = useState<Cliente | null>(null)

  // Estados de edição
  const [editandoDados, setEditandoDados] = useState(false)
  const [editandoDJE, setEditandoDJE] = useState(false)

  // Estados do certificado DEC
  const [uploadingCert, setUploadingCert] = useState(false)
  const [senhaCertificado, setSenhaCertificado] = useState('')
  const [arquivoCertificado, setArquivoCertificado] = useState<File | null>(null)
  const [consultandoDec, setConsultandoDec] = useState(false)

  // Estados DJE
  const [testandoDje, setTestandoDje] = useState(false)
  const [consultandoDje, setConsultandoDje] = useState(false)
  const [modalDjeAberto, setModalDjeAberto] = useState(false)

  // Estados do Histórico
  const [historico, setHistorico] = useState<HistoricoConsulta[]>([])
  const [historicoLoading, setHistoricoLoading] = useState(false)
  const [historicoPagina, setHistoricoPagina] = useState(1)
  const [historicoTotal, setHistoricoTotal] = useState(0)
  const [historicoTotalPages, setHistoricoTotalPages] = useState(0)

  // Formulário de dados básicos
  const [formDados, setFormDados] = useState({
    razao_social: '',
    nome_fantasia: '',
    email_principal: '',
    telefone: '',
    ativo: true,
  })

  // Formulário DJE (agora usa tenant_id)
  const [formDJE, setFormDJE] = useState({
    dje_tenant_id: '',
    dje_ativo: false,
  })

  // Formatar CNPJ
  const formatarCNPJ = (cnpj?: string | null): string => {
    if (!cnpj) return ''
    const numeros = cnpj.replace(/\D/g, '')
    if (numeros.length !== 14) return cnpj
    return numeros.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')
  }

  // Carregar cliente - useCallback para evitar re-renders
  const carregarCliente = useCallback(async () => {
    if (!id) return

    try {
      const data = await clientesService.buscarPorId(id)
      setCliente(data)

      // Preencher formulários apenas se não estiver editando
      if (!editandoDados) {
        setFormDados({
          razao_social: data.razao_social || '',
          nome_fantasia: data.nome_fantasia || '',
          email_principal: data.email_principal || '',
          telefone: data.telefone || '',
          ativo: data.ativo ?? true,
        })
      }

      if (!editandoDJE) {
        setFormDJE({
          dje_tenant_id: data.dje_tenant_id || '',
          dje_ativo: data.dje_ativo ?? false,
        })
      }
    } catch (error) {
      console.error('Erro ao carregar cliente:', error)
    }
  }, [id, editandoDados, editandoDJE])

  // Carregar inicial
  const carregarClienteInicial = async () => {
    if (!id) return

    setLoading(true)
    try {
      const data = await clientesService.buscarPorId(id)
      setCliente(data)

      setFormDados({
        razao_social: data.razao_social || '',
        nome_fantasia: data.nome_fantasia || '',
        email_principal: data.email_principal || '',
        telefone: data.telefone || '',
        ativo: data.ativo ?? true,
      })

      setFormDJE({
        dje_tenant_id: data.dje_tenant_id || '',
        dje_ativo: data.dje_ativo ?? false,
      })
    } catch (error) {
      console.error('Erro ao carregar cliente:', error)
      toast.error('Erro ao carregar cliente')
      navigate('/clientes')
    } finally {
      setLoading(false)
    }
  }

  // Carregar histórico de consultas
  const carregarHistorico = async (pagina: number = 1) => {
    if (!id) return

    setHistoricoLoading(true)
    try {
      const result = await clientesService.listarHistorico(id, pagina, 10)
      setHistorico(result.data)
      setHistoricoTotal(result.pagination.total)
      setHistoricoTotalPages(result.pagination.totalPages)
      setHistoricoPagina(pagina)
    } catch (error) {
      console.error('Erro ao carregar histórico:', error)
    } finally {
      setHistoricoLoading(false)
    }
  }

  useEffect(() => {
    carregarClienteInicial()
    carregarHistorico()
  }, [id])

  // Auto-refresh controlado - só ativa quando tem PROCESSANDO
  useEffect(() => {
    const temProcessando = historico.some(h => h.status === 'PROCESSANDO')
    const estaEditando = editandoDados || editandoDJE
    
    // Limpar intervalo anterior
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current)
      refreshIntervalRef.current = null
    }
    
    // Só ativar se tem processando E não está editando
    if (temProcessando && !estaEditando) {
      refreshIntervalRef.current = setInterval(() => {
        carregarHistorico(historicoPagina)
        carregarCliente()
      }, 5000)
    }
    
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current)
      }
    }
  }, [historico.length, editandoDados, editandoDJE, historicoPagina, carregarCliente])

  // Toggle DEC ativo
  const toggleDecAtivo = async (ativo: boolean) => {
    if (!id) return
    try {
      await clientesService.toggleDec(id, ativo)
      toast.success(ativo ? 'DEC ativado' : 'DEC desativado')
      carregarCliente()
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao alterar status')
    }
  }

  // Toggle DJE ativo
  const toggleDjeAtivo = async (ativo: boolean) => {
    if (!id) return
    try {
      await clientesService.toggleDje(id, ativo)
      toast.success(ativo ? 'Monitoramento DJE ativado' : 'Monitoramento DJE desativado')
      carregarCliente()
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao alterar status')
    }
  }

  // Salvar dados básicos
  const salvarDados = async () => {
    if (!id) return

    setSaving(true)
    try {
      await clientesService.atualizar(id, formDados)
      toast.success('Dados atualizados')
      setEditandoDados(false)
      carregarCliente()
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao salvar')
    } finally {
      setSaving(false)
    }
  }

  // Salvar configuração DJE
  const salvarDJE = async () => {
    if (!id) return

    setSaving(true)
    try {
      await clientesService.atualizarDJE(id, {
        dje_tenant_id: formDJE.dje_tenant_id || null,
        dje_ativo: formDJE.dje_ativo,
      })
      toast.success('Configuração DJE atualizada')
      setEditandoDJE(false)
      carregarCliente()
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao salvar')
    } finally {
      setSaving(false)
    }
  }

  // Testar conexão DJE
  const testarConexaoDje = async () => {
    if (!id) return

    setTestandoDje(true)
    try {
      const result = await clientesService.testarDJE(id)
      if (result.sucesso) {
        toast.success('Conexão DJE OK')
      } else {
        toast.error(result.mensagem || 'Falha na conexão DJE')
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao testar conexão')
    } finally {
      setTestandoDje(false)
    }
  }

  // Consultar DJE manualmente
  const consultarDjeManual = async () => {
    if (!id) return

    setConsultandoDje(true)
    try {
      const result = await clientesService.consultarDJE(id)
      if (result.sucesso) {
        toast.success(`Consulta DJE: ${result.novas || 0} nova(s) comunicação(ões)`)
      } else {
        toast.error(result.mensagem || 'Falha na consulta DJE')
      }
      carregarCliente()
      carregarHistorico()
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro na consulta DJE')
    } finally {
      setConsultandoDje(false)
    }
  }

  // Consultar DEC manualmente (ASSÍNCRONO)
  const consultarDecManual = async () => {
    if (!id) return

    setConsultandoDec(true)
    
    try {
      const result = await clientesService.consultarDec(id)
      
      if (result.success) {
        toast.success('Consulta DEC iniciada! Acompanhe o progresso no histórico.')
        carregarHistorico()
      } else {
        toast.error(result.error || 'Falha ao iniciar consulta DEC')
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.error || 'Erro ao iniciar consulta DEC'
      toast.error(errorMsg)
    } finally {
      setConsultandoDec(false)
    }
  }

  // Upload certificado DEC
  const handleUploadCertificado = async () => {
    if (!id || !arquivoCertificado || !senhaCertificado) {
      toast.error('Selecione o arquivo e digite a senha do certificado')
      return
    }

    setUploadingCert(true)
    try {
      await clientesService.uploadCertificado(id, arquivoCertificado, senhaCertificado)
      toast.success('Certificado enviado com sucesso')
      setArquivoCertificado(null)
      setSenhaCertificado('')
      if (fileInputRef.current) fileInputRef.current.value = ''
      carregarCliente()
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao enviar certificado')
    } finally {
      setUploadingCert(false)
    }
  }

  // Remover certificado
  const handleRemoverCertificado = async () => {
    if (!id) return
    
    if (!confirm('Tem certeza que deseja remover o certificado? O monitoramento DEC será desativado.')) {
      return
    }

    setUploadingCert(true)
    try {
      await clientesService.removerCertificado(id)
      toast.success('Certificado removido')
      carregarCliente()
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao remover certificado')
    } finally {
      setUploadingCert(false)
    }
  }

  // Status do certificado
  const getStatusCertificado = () => {
    if (!cliente?.certificado_status) return null
    
    const statusMap: Record<string, { cor: string; texto: string; icon: typeof CheckCircle }> = {
      VALIDO: { cor: 'bg-emerald-50 text-emerald-700', texto: 'Válido', icon: CheckCircle },
      PENDENTE: { cor: 'bg-amber-50 text-amber-700', texto: 'Pendente', icon: Loader2 },
      VALIDANDO: { cor: 'bg-blue-50 text-blue-700', texto: 'Validando...', icon: Loader2 },
      EXPIRADO: { cor: 'bg-red-50 text-red-700', texto: 'Expirado', icon: AlertTriangle },
      INVALIDO: { cor: 'bg-red-50 text-red-700', texto: 'Inválido', icon: XCircle },
      ERRO: { cor: 'bg-red-50 text-red-700', texto: 'Erro', icon: XCircle },
    }
    
    return statusMap[cliente.certificado_status] || statusMap.PENDENTE
  }

  // Formatar duração em ms
  const formatarDuracao = (ms?: number | null): string => {
    if (!ms) return '-'
    if (ms < 1000) return `${ms}ms`
    const segundos = Math.round(ms / 1000)
    if (segundos < 60) return `${segundos}s`
    const minutos = Math.floor(segundos / 60)
    const segs = segundos % 60
    return `${minutos}m ${segs}s`
  }

  // ============================================
  // LOADING STATE
  // ============================================
  if (loading) {
    return (
      <AppLayout pagina="Carregando...">
        <PageContainer>
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        </PageContainer>
      </AppLayout>
    )
  }

  // ============================================
  // CLIENTE NÃO ENCONTRADO
  // ============================================
  if (!cliente) {
    return (
      <AppLayout pagina="Cliente não encontrado">
        <PageContainer>
          <PageSection>
            <div className="text-center py-12">
              <p className="text-gray-500 mb-4">Cliente não encontrado</p>
              <button
                onClick={() => navigate('/clientes')}
                className="px-4 py-2 bg-[#0F1B3D] text-white rounded-lg"
              >
                Voltar para Clientes
              </button>
            </div>
          </PageSection>
        </PageContainer>
      </AppLayout>
    )
  }

  // ============================================
  // RENDERIZAÇÃO PRINCIPAL
  // ============================================
  const statusCert = getStatusCertificado()

  return (
    <AppLayout pagina={cliente.razao_social}>
      <PageContainer>
        {/* Modal de Instruções DJE */}
        <ModalInstrucoesDJE 
          isOpen={modalDjeAberto} 
          onClose={() => setModalDjeAberto(false)} 
        />

        {/* Banner */}
        <PageBanner
          titulo={cliente.razao_social}
          subtitulo={`CNPJ: ${formatarCNPJ(cliente.cnpj)}`}
          icon={<Building2 className="w-6 h-6" />}
          acoes={
            <>
              <PageBanner.BotaoSecundario 
                icon={<ArrowLeft className="w-4 h-4" />} 
                onClick={() => navigate('/clientes')}
              >
                Voltar
              </PageBanner.BotaoSecundario>
              <PageBanner.BotaoSecundario 
                icon={<RefreshCw className="w-4 h-4" />} 
                onClick={carregarClienteInicial} 
                loading={loading}
              >
                Atualizar
              </PageBanner.BotaoSecundario>
            </>
          }
        />

        {/* Dados Básicos */}
        <PageSection
          titulo="Dados do Cliente"
          icon={<Building2 className="w-5 h-5" />}
          headerActions={
            !editandoDados ? (
              <button
                onClick={() => setEditandoDados(true)}
                className="text-sm text-[#0F1B3D] hover:underline"
              >
                Editar
              </button>
            ) : null
          }
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">
                Razão Social
              </label>
              {editandoDados ? (
                <input
                  type="text"
                  value={formDados.razao_social}
                  onChange={(e) => setFormDados((prev) => ({ ...prev, razao_social: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              ) : (
                <p className="text-gray-900">{cliente.razao_social}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">
                Nome Fantasia
              </label>
              {editandoDados ? (
                <input
                  type="text"
                  value={formDados.nome_fantasia}
                  onChange={(e) => setFormDados((prev) => ({ ...prev, nome_fantasia: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              ) : (
                <p className="text-gray-900">{cliente.nome_fantasia || '-'}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">
                Email Principal
              </label>
              {editandoDados ? (
                <input
                  type="email"
                  value={formDados.email_principal}
                  onChange={(e) => setFormDados((prev) => ({ ...prev, email_principal: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              ) : (
                <p className="text-gray-900 flex items-center gap-2">
                  {cliente.email_principal ? (
                    <>
                      <Mail className="w-4 h-4 text-gray-400" />
                      {cliente.email_principal}
                    </>
                  ) : (
                    '-'
                  )}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">
                Telefone
              </label>
              {editandoDados ? (
                <input
                  type="tel"
                  value={formDados.telefone}
                  onChange={(e) => setFormDados((prev) => ({ ...prev, telefone: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              ) : (
                <p className="text-gray-900 flex items-center gap-2">
                  {cliente.telefone ? (
                    <>
                      <Phone className="w-4 h-4 text-gray-400" />
                      {cliente.telefone}
                    </>
                  ) : (
                    '-'
                  )}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">
                Status
              </label>
              {editandoDados ? (
                <select
                  value={formDados.ativo ? 'true' : 'false'}
                  onChange={(e) => setFormDados((prev) => ({ ...prev, ativo: e.target.value === 'true' }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="true">Ativo</option>
                  <option value="false">Inativo</option>
                </select>
              ) : (
                <span
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
                    cliente.ativo ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {cliente.ativo ? (
                    <CheckCircle className="w-3.5 h-3.5" />
                  ) : (
                    <XCircle className="w-3.5 h-3.5" />
                  )}
                  {cliente.ativo ? 'Ativo' : 'Inativo'}
                </span>
              )}
            </div>
          </div>

          {editandoDados && (
            <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t">
              <button
                onClick={() => setEditandoDados(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Cancelar
              </button>
              <button
                onClick={salvarDados}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-[#0F1B3D] rounded-lg flex items-center gap-2"
                style={{ backgroundColor: '#FFB800' }}
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                <Save className="w-4 h-4" />
                Salvar
              </button>
            </div>
          )}
        </PageSection>

        {/* Certificado Digital DEC (SEFAZ-SP) */}
        <PageSection
          titulo="Certificado Digital DEC"
          subtitulo="Certificado A1 para consulta automática da DEC SEFAZ-SP"
          icon={<Shield className="w-5 h-5" />}
        >
          {cliente.certificado_cn && cliente.certificado_status !== 'PENDENTE' && statusCert ? (
            <div className="space-y-4">
              {/* Info do Certificado */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-4">
                  <FileKey className="w-10 h-10 text-[#0F1B3D]" />
                  <div>
                    <p className="font-medium text-gray-900">
                      {cliente.certificado_cn || 'Certificado Digital'}
                    </p>
                    {cliente.certificado_validade && (
                      <p className="text-sm text-gray-500">
                        Válido até: {new Date(cliente.certificado_validade).toLocaleDateString('pt-BR')}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${statusCert.cor}`}>
                    <statusCert.icon className="w-3.5 h-3.5" />
                    {statusCert.texto}
                  </span>
                  <button
                    onClick={handleRemoverCertificado}
                    disabled={uploadingCert}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Remover certificado"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Toggle Monitoramento DEC */}
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">Monitoramento DEC</p>
                  <p className="text-sm text-gray-500">Consultar automaticamente a DEC SEFAZ-SP</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={cliente.dec_ativo || false}
                    onChange={(e) => toggleDecAtivo(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#14B8A6]"></div>
                </label>
              </div>

              {/* Última consulta e botão Consultar Agora */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Clock className="w-4 h-4" />
                  {cliente.dec_ultima_consulta ? (
                    <span>Última consulta: {new Date(cliente.dec_ultima_consulta).toLocaleString('pt-BR')}</span>
                  ) : (
                    <span>Nenhuma consulta realizada</span>
                  )}
                </div>
                <button
                  onClick={consultarDecManual}
                  disabled={consultandoDec || !cliente.dec_ativo || cliente.certificado_status !== 'VALIDO'}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#0F1B3D] rounded-lg transition-all ${
                    consultandoDec ? 'opacity-90 cursor-wait' : 'disabled:opacity-50'
                  }`}
                  style={{ backgroundColor: '#FFB800' }}
                >
                  {consultandoDec ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Consultando...</span>
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4" />
                      <span>Consultar Agora</span>
                    </>
                  )}
                </button>
              </div>

              {/* Erro DEC */}
              {cliente.dec_ultimo_erro && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-red-800">Erro na última consulta DEC</p>
                      <p className="text-sm text-red-600 mt-1">{cliente.dec_ultimo_erro}</p>
                      {cliente.consultas_com_erro && cliente.consultas_com_erro > 1 && (
                        <p className="text-xs text-red-500 mt-1">
                          Erros consecutivos: {cliente.consultas_com_erro}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <Upload className="w-10 h-10 mx-auto mb-3 text-gray-400" />
                <p className="text-gray-600 mb-2">
                  Selecione o certificado digital A1 (.pfx ou .p12)
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pfx,.p12"
                  onChange={(e) => setArquivoCertificado(e.target.files?.[0] || null)}
                  className="hidden"
                  id="cert-upload"
                />
                <label
                  htmlFor="cert-upload"
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg cursor-pointer"
                  style={{ backgroundColor: '#0F1B3D' }}
                >
                  <Upload className="w-4 h-4" />
                  Selecionar Arquivo
                </label>
              </div>

              {arquivoCertificado && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <FileKey className="w-5 h-5 text-[#0F1B3D]" />
                      <span className="text-sm text-gray-700">{arquivoCertificado.name}</span>
                    </div>
                    <button
                      onClick={() => {
                        setArquivoCertificado(null)
                        if (fileInputRef.current) fileInputRef.current.value = ''
                      }}
                      className="p-1 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Senha do Certificado
                    </label>
                    <input
                      type="password"
                      value={senhaCertificado}
                      onChange={(e) => setSenhaCertificado(e.target.value)}
                      placeholder="Digite a senha do certificado"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>

                  <button
                    onClick={handleUploadCertificado}
                    disabled={uploadingCert || !senhaCertificado}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-[#0F1B3D] rounded-lg disabled:opacity-50"
                    style={{ backgroundColor: '#FFB800' }}
                  >
                    {uploadingCert ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4" />
                    )}
                    Enviar Certificado
                  </button>
                </div>
              )}
            </div>
          )}
        </PageSection>

        {/* Monitoramento DJE */}
        <PageSection
          titulo={
            <div className="flex items-center gap-2">
              <span>Monitoramento DJE</span>
              <button
                onClick={() => setModalDjeAberto(true)}
                className="p-1 text-gray-400 hover:text-[#0F1B3D] hover:bg-gray-100 rounded-full transition-colors"
                title="Como obter as credenciais DJE"
              >
                <HelpCircle className="w-4 h-4" />
              </button>
            </div>
          }
          subtitulo="Domicílio Judicial Eletrônico - Consulta de citações e intimações"
          icon={<Scale className="w-5 h-5" />}
          headerActions={
            !editandoDJE ? (
              <button
                onClick={() => setEditandoDJE(true)}
                className="text-sm text-[#0F1B3D] hover:underline"
              >
                Configurar
              </button>
            ) : null
          }
        >
          {editandoDJE ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tenant ID (PJe)
                </label>
                <input
                  type="text"
                  value={formDJE.dje_tenant_id}
                  onChange={(e) => setFormDJE((prev) => ({ ...prev, dje_tenant_id: e.target.value }))}
                  placeholder="Ex: 11568038000181"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Geralmente é o CNPJ sem pontuação. 
                  <button 
                    onClick={() => setModalDjeAberto(true)}
                    className="text-blue-600 hover:underline ml-1"
                  >
                    Como obter?
                  </button>
                </p>
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={formDJE.dje_ativo}
                  onChange={(e) => setFormDJE((prev) => ({ ...prev, dje_ativo: e.target.checked }))}
                  id="dje-ativo"
                  className="w-4 h-4 rounded border-gray-300 text-[#0F1B3D] focus:ring-[#0F1B3D]"
                />
                <label htmlFor="dje-ativo" className="text-sm text-gray-700">
                  Ativar monitoramento DJE
                </label>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t">
                <button
                  onClick={() => setEditandoDJE(false)}
                  className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  onClick={salvarDJE}
                  disabled={saving}
                  className="px-4 py-2 text-sm font-medium text-[#0F1B3D] rounded-lg flex items-center gap-2"
                  style={{ backgroundColor: '#FFB800' }}
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  <Save className="w-4 h-4" />
                  Salvar
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Status atual */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-4">
                  <Scale className="w-10 h-10 text-[#0F1B3D]" />
                  <div>
                    <p className="font-medium text-gray-900">
                      {cliente.dje_tenant_id 
                        ? `Tenant: ${cliente.dje_tenant_id}`
                        : 'Tenant ID não configurado'}
                    </p>
                    {cliente.dje_ultima_consulta && (
                      <p className="text-sm text-gray-500">
                        Última consulta: {new Date(cliente.dje_ultima_consulta).toLocaleString('pt-BR')}
                      </p>
                    )}
                  </div>
                </div>
                <span
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
                    cliente.dje_ativo 
                      ? 'bg-emerald-50 text-emerald-700' 
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {cliente.dje_ativo ? (
                    <CheckCircle className="w-3.5 h-3.5" />
                  ) : (
                    <XCircle className="w-3.5 h-3.5" />
                  )}
                  {cliente.dje_ativo ? 'Ativo' : 'Inativo'}
                </span>
              </div>

              {/* Toggle + Ações */}
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">Monitoramento DJE</p>
                  <p className="text-sm text-gray-500">Consultar automaticamente citações e intimações</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={cliente.dje_ativo || false}
                    onChange={(e) => toggleDjeAtivo(e.target.checked)}
                    disabled={!cliente.dje_tenant_id}
                    className="sr-only peer"
                  />
                  <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#14B8A6] ${!cliente.dje_tenant_id ? 'opacity-50 cursor-not-allowed' : ''}`}></div>
                </label>
              </div>

              {/* Botões de ação */}
              {cliente.dje_tenant_id && (
                <div className="flex items-center gap-3">
                  <button
                    onClick={testarConexaoDje}
                    disabled={testandoDje}
                    className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                  >
                    {testandoDje ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Wifi className="w-4 h-4" />
                    )}
                    Testar Conexão
                  </button>
                  <button
                    onClick={consultarDjeManual}
                    disabled={consultandoDje || !cliente.dje_ativo}
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#0F1B3D] rounded-lg transition-all ${
                      consultandoDje ? 'opacity-90 cursor-wait' : 'disabled:opacity-50'
                    }`}
                    style={{ backgroundColor: '#FFB800' }}
                  >
                    {consultandoDje ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Consultando...</span>
                      </>
                    ) : (
                      <>
                        <Play className="w-4 h-4" />
                        <span>Consultar Agora</span>
                      </>
                    )}
                  </button>
                </div>
              )}

              {/* Erro DJE */}
              {cliente.dje_ultimo_erro && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-red-800">Erro na última consulta</p>
                      <p className="text-sm text-red-600">{cliente.dje_ultimo_erro}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Info */}
              {!cliente.dje_tenant_id && (
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Configure o Tenant ID</strong> para habilitar o monitoramento DJE.
                    <button 
                      onClick={() => setModalDjeAberto(true)}
                      className="text-blue-600 hover:underline ml-1"
                    >
                      Clique aqui para ver como obter.
                    </button>
                  </p>
                </div>
              )}
            </div>
          )}
        </PageSection>

        {/* Histórico de Consultas */}
        <PageSection
          titulo="Histórico de Consultas"
          subtitulo="Últimas consultas DEC e DJE realizadas para este cliente"
          icon={<History className="w-5 h-5" />}
          headerActions={
            <button
              onClick={() => carregarHistorico(historicoPagina)}
              disabled={historicoLoading}
              className="text-sm text-[#0F1B3D] hover:underline flex items-center gap-1"
            >
              {historicoLoading ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <RefreshCw className="w-3 h-3" />
              )}
              Atualizar
            </button>
          }
        >
          {historicoLoading && historico.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : historico.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <History className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              <p>Nenhuma consulta realizada ainda</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Tabela de Histórico */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-2 font-medium text-gray-600">Data</th>
                      <th className="text-left py-3 px-2 font-medium text-gray-600">Tipo</th>
                      <th className="text-left py-3 px-2 font-medium text-gray-600">Status</th>
                      <th className="text-left py-3 px-2 font-medium text-gray-600">Encontradas</th>
                      <th className="text-left py-3 px-2 font-medium text-gray-600">Novas</th>
                      <th className="text-left py-3 px-2 font-medium text-gray-600">Tempo</th>
                      <th className="text-left py-3 px-2 font-medium text-gray-600">Erro</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historico.map((item) => (
                      <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-2 text-gray-700">
                          {item.data_consulta 
                            ? new Date(item.data_consulta).toLocaleString('pt-BR')
                            : '-'}
                        </td>
                        <td className="py-3 px-2">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            item.tipo === 'DEC_SEFAZ' 
                              ? 'bg-orange-100 text-orange-700' 
                              : 'bg-purple-100 text-purple-700'
                          }`}>
                            {item.tipo === 'DEC_SEFAZ' ? 'DEC' : 'DJE'}
                          </span>
                        </td>
                        <td className="py-3 px-2">
                          {item.status === 'PROCESSANDO' ? (
                            <span className="inline-flex items-center gap-1 text-blue-600">
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Processando...
                            </span>
                          ) : item.sucesso ? (
                            <span className="inline-flex items-center gap-1 text-emerald-600">
                              <CheckCircle className="w-4 h-4" />
                              Sucesso
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-red-600">
                              <XCircle className="w-4 h-4" />
                              Erro
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-2 text-gray-700">
                          {item.comunicacoes_encontradas || 0}
                        </td>
                        <td className="py-3 px-2">
                          {item.novas_comunicacoes > 0 ? (
                            <span className="text-emerald-600 font-medium">
                              +{item.novas_comunicacoes}
                            </span>
                          ) : (
                            <span className="text-gray-400">0</span>
                          )}
                        </td>
                        <td className="py-3 px-2 text-gray-500">
                          {formatarDuracao(item.tempo_execucao_ms)}
                        </td>
                        <td className="py-3 px-2">
                          {item.erro ? (
                            <span className="text-red-600 text-xs truncate max-w-[200px] block" title={item.erro}>
                              {item.erro.substring(0, 50)}...
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Paginação */}
              {historicoTotalPages > 1 && (
                <div className="flex items-center justify-between pt-4 border-t">
                  <span className="text-sm text-gray-500">
                    {historicoTotal} consulta(s) no total
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => carregarHistorico(historicoPagina - 1)}
                      disabled={historicoPagina <= 1 || historicoLoading}
                      className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="text-sm text-gray-600">
                      {historicoPagina} / {historicoTotalPages}
                    </span>
                    <button
                      onClick={() => carregarHistorico(historicoPagina + 1)}
                      disabled={historicoPagina >= historicoTotalPages || historicoLoading}
                      className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </PageSection>

        {/* ============================================
         * SEÇÃO DE NOTIFICAÇÕES - OCULTA
         * A notificação vai para o escritório, não para o cliente individual
         * Para reativar, descomente o bloco abaixo
         * ============================================
         */}

      </PageContainer>
    </AppLayout>
  )
}

